/**
 * Admin finance, support, safety, risk, and audit (S114; M12). Wires the operator-gated S094 finance surface
 * (reconcile, payout) and the S096 case surface (support queue/assign/actions, safety + risk transitions, and
 * the append-only case audit — the drill) to the console. RBAC + scope + redaction are enforced server-side;
 * a refund/coupon **above the operator's financial limit** comes back as an escalation (403), which the client
 * surfaces as an escalation prompt rather than a raw error. A case can be **cross-linked to its ride timeline**
 * for context. The audit trail is immutable (read-only) — the console displays it, never edits it.
 */
import { type FinanceApi, type CaseAdminApi, type ReconcileResult, type TicketActionInput, type AuditEntry, type CaseType, type RideSupportApi, type ApiError } from '@vello/api-client';
import { type Result } from '@vello/core';
import { type OperatorRideTimeline, loadRideTimeline } from './ops-console.js';

interface FinanceRuntime {
  readonly finance: FinanceApi;
}
interface CaseRuntime {
  readonly cases: CaseAdminApi;
  readonly rideSupport: RideSupportApi;
}

// ---- finance -------------------------------------------------------------------------------------

/** Reconcile ONE market (MR-A Codex round-1 P1-3): the server rejects an unscoped reconciliation. */
export function runReconciliation(runtime: FinanceRuntime, marketId: string): Promise<Result<ReconcileResult, ApiError>> {
  return runtime.finance.reconcile(marketId);
}

/** D412: `idempotencyKey` names the issue attempt — a retry with it replays the payout instead of issuing another. */
export function issuePayout(runtime: FinanceRuntime, input: { driverId: string; amountMinor: string; currency?: string; idempotencyKey: string }): Promise<Result<unknown, ApiError>> {
  return runtime.finance.createPayout(input);
}

// ---- support actions with escalation -------------------------------------------------------------

export interface ActionOutcome {
  readonly ok: boolean;
  /** True when the action needs escalation (above the operator's financial limit — server 403). */
  readonly escalationRequired: boolean;
  readonly messageId?: string;
  /**
   * D384: what the server actually did — `TRANSITION` (a reopen: executed), `INTENT` (a refund/coupon the money layer
   * has NOT executed; nothing was paid back) or `AUDIT` (only an audit row). The console words its notice from this,
   * never "action applied" for an intent.
   */
  readonly recorded?: 'TRANSITION' | 'INTENT' | 'AUDIT';
  readonly executed?: boolean;
  /** The server's stable refusal reason (e.g. `SUPPORT_MONEY_ACTIONS_CASH_ONLY_MARKET`). */
  readonly reasonCode?: string;
}

/**
 * Apply a support action. A refund/coupon above the operator's financial limit is refused server-side (403);
 * the client surfaces that as an escalation prompt, not a failure.
 */
export async function applyTicketAction(
  runtime: CaseRuntime,
  ticketId: string,
  action: TicketActionInput,
): Promise<ActionOutcome> {
  const res = await runtime.cases.ticketAction(ticketId, action);
  if (res.ok) {
    const v = (typeof res.value === 'object' && res.value !== null ? res.value : {}) as { recorded?: unknown; executed?: unknown };
    const recorded = v.recorded === 'TRANSITION' || v.recorded === 'INTENT' || v.recorded === 'AUDIT' ? v.recorded : undefined;
    return { ok: true, escalationRequired: false, ...(recorded !== undefined ? { recorded, executed: v.executed === true } : {}) };
  }
  if (res.error.code === 'FORBIDDEN') return { ok: false, escalationRequired: true, messageId: 'admin.cases.escalationRequired' };
  return { ok: false, escalationRequired: false, messageId: 'admin.cases.actionFailed', ...(res.error.reasonCode !== undefined ? { reasonCode: res.error.reasonCode } : {}) };
}

// ---- audit + timeline cross-link -----------------------------------------------------------------

export function loadCaseAudit(runtime: CaseRuntime, type: CaseType, caseId: string): Promise<Result<{ entries: readonly AuditEntry[] }, ApiError>> {
  return runtime.cases.caseAudit(type, caseId);
}

/**
 * Cross-link a case to its ride timeline for context. D364: through the operator-authorized ride-support read
 * (`ride:view` at the ride's market), not the participant-only execution timeline that refused every operator.
 */
export function crossLinkTimeline(runtime: CaseRuntime, rideId: string): Promise<Result<OperatorRideTimeline, ApiError>> {
  return loadRideTimeline(runtime, rideId);
}

// ---- audit view (read-only, append-only) ---------------------------------------------------------

// D401: `describeAuditTrail` (a wrapper that only counted the entries) had no caller and was removed.
